import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AppShell from "@/components/AppShell";
import AddProspectSheet from "@/components/AddProspectSheet";
import MagicProspectButton from "@/components/MagicProspectButton";
import WelcomeSplash from "@/components/WelcomeSplash";
import PrepareActionSheet from "@/components/PrepareActionSheet";
import { Phone, Calendar, ArrowRight, Plus, Clock, TrendingUp, Shield, ChevronRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

/* ───────────────────────────────────────────────────────────────
   Aujourd'hui — page d'arrivée. Inspirée de Pipedrive / Apollo.
   Une seule colonne, deux blocs :
     1. KPI bar (3 chiffres business)
     2. Inbox commercial dense (actions du jour priorisées)
   Rien d'autre. C'est la table de cockpit.
   ─────────────────────────────────────────────────────────────── */

const TAGS = {
  rdv:     { label: "RDV",     cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  relance: { label: "RELANCE", cls: "bg-rose-500/10 text-rose-300 border-rose-500/30" },
  contact: { label: "À FAIRE", cls: "bg-amber-500/10 text-amber-200 border-amber-500/30" },
  hot:     { label: "HOT",     cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
};
const ACTION_ICON = { rdv: Calendar, relance: Clock, contact: Phone, hot: TrendingUp };
const PREPARE_TYPE = { rdv: "rdv", relance: "email", contact: "call", hot: "call" };

function fmtMoney(n) {
  if (!n || n < 1000) return `${Math.round(n || 0)} €`;
  if (n < 1_000_000) return `${(n / 1000).toFixed(n >= 100_000 ? 0 : 1)} k€`;
  return `${(n / 1_000_000).toFixed(1)} M€`;
}

function whyForItem(item) {
  const p = item.prospect;
  if (item.type === "rdv") {
    const time = new Date(item.ts).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    return `RDV à ${time} · ${p.company || "—"}`;
  }
  if (item.type === "relance") {
    const days = Math.floor((Date.now() - item.ts) / 86400000);
    return item.ts ? `Pas de contact depuis ${days} j` : "Jamais contacté";
  }
  if (item.type === "hot") return `Score ${p.score}% · ${p.company || "—"}`;
  const r = [];
  if (p.company) r.push(p.company);
  if (p.score) r.push(`score ${p.score}%`);
  return r.join(" · ") || "À traiter aujourd'hui";
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prospects, setProspects] = useState(null);
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [prepareTarget, setPrepareTarget] = useState(null); // { prospectId, prospect, actionType }

  useEffect(() => {
    api.get("/admin/me").then((r) => setIsAdmin(!!r.data?.is_admin)).catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await api.post("/seed").catch(() => {});
        const [pRes, sRes, prRes] = await Promise.all([
          api.get("/prospects"),
          api.get("/stats/dashboard").catch(() => ({ data: null })),
          api.get("/company-profile").catch(() => ({ data: null })),
        ]);
        setProspects(pRes.data || []);
        setStats(sRes.data);
        setProfile(prRes.data);
      } catch {
        toast.error("Erreur chargement");
        setProspects([]);
      }
    })();
  }, []);

  useEffect(() => {
    const open = () => setAddOpen(true);
    window.addEventListener("openAddProspect", open);
    return () => window.removeEventListener("openAddProspect", open);
  }, []);

  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const dateLabel = today.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  const firstName = (user?.name || "").split(" ")[0] || "";
  const hour = today.getHours();
  const greeting = hour >= 18 ? "Bonsoir" : hour >= 13 ? "Bon après-midi" : "Bonjour";

  const avgBasket = profile?.average_basket || 8000;
  const openProspects = (prospects || []).filter((p) => !["Sans suite", "Converti"].includes(p.status));
  const pipelineValue = openProspects.length * avgBasket;
  const dealsOpen = openProspects.length;
  const activity7d = stats?.prospects_week ?? 0;

  /* ─── Inbox commercial : tout ce qu'on doit faire aujourd'hui ─── */
  const inbox = useMemo(() => {
    if (!prospects) return [];
    const items = [];
    const now = new Date();

    prospects
      .filter((p) => (p.rdv_at || "").slice(0, 10) === todayStr)
      .forEach((p) => items.push({ type: "rdv", prospect: p, ts: new Date(p.rdv_at).getTime(), priority: 1 }));

    prospects
      .filter((p) => ["Contacté", "En discussion"].includes(p.status))
      .forEach((p) => {
        const last = p.last_interaction_at || p.updated_at || p.created_at || "";
        const days = last ? Math.floor((now - new Date(last)) / 86400000) : 999;
        if (!last || days >= 7) {
          items.push({ type: "relance", prospect: p, ts: last ? new Date(last).getTime() : 0, priority: 2 });
        }
      });

    prospects
      .filter((p) => p.status === "À contacter" && (p.score || 0) >= 80)
      .sort((a, b) => (b.score || 0) - (a.score || 0))
      .forEach((p) => items.push({ type: "hot", prospect: p, ts: new Date(p.created_at || Date.now()).getTime(), priority: 1 }));

    prospects
      .filter((p) => p.status === "À contacter" && (p.score || 0) < 80)
      .sort((a, b) => (b.score || 0) - (a.score || 0))
      .slice(0, 10)
      .forEach((p) => items.push({ type: "contact", prospect: p, ts: new Date(p.created_at || Date.now()).getTime(), priority: 3 }));

    return items
      .sort((a, b) => (a.priority - b.priority) || (b.ts - a.ts))
      .filter((it, i, arr) => arr.findIndex((x) => x.prospect.prospect_id === it.prospect.prospect_id) === i)
      .slice(0, 20);
  }, [prospects, todayStr]);

  if (!prospects) {
    return (
      <AppShell>
        <div className="grid place-items-center py-24">
          <div className="w-8 h-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
        </div>
      </AppShell>
    );
  }

  const totalLeads = prospects.length;

  return (
    <AppShell>
      {/* Header — editorial 2026 */}
      <div className="px-5 pt-7 pb-4 pr-16">
        <div className="text-[10px] text-zinc-500 capitalize uppercase tracking-[0.3em] mb-2">{dateLabel}</div>
        <h1 className="h-editorial text-[34px] sm:text-[42px] text-[var(--text)]" data-testid="dashboard-greeting">
          {greeting} {firstName}.
        </h1>
        <p className="text-[12.5px] text-zinc-400 mt-2 leading-snug max-w-md">
          {inbox.length > 0
            ? <><span className="text-emerald-300 font-semibold tnum">{inbox.length}</span> action{inbox.length > 1 ? "s" : ""} pour aujourd'hui · IA prête à préparer chacune en un clic.</>
            : "Aucune action prioritaire. Direction l'onglet Trouver pour ajouter des prospects."}
        </p>
        <div className="hairline-gradient mt-4 -mr-5" />
      </div>

      {/* KPI bento — 3 tiles, 2026 layout */}
      {totalLeads > 0 && (
        <div className="px-5 mb-6">
          <div className="grid grid-cols-3 gap-2" data-testid="kpi-bar">
            <button onClick={() => navigate("/pipeline")} className="bento-tile px-3.5 py-4 text-left" data-testid="kpi-pipeline">
              <div className="text-[9.5px] text-zinc-500 uppercase tracking-[0.25em] mb-1">Pipeline</div>
              <div className="h-editorial text-[22px] text-white tnum">{fmtMoney(pipelineValue)}</div>
              <div className="mt-2 flex items-center gap-1">
                <span className="w-1 h-1 rounded-full bg-emerald-400 spark-pulse" />
                <span className="text-[9.5px] text-zinc-500 tracking-wider">EN COURS</span>
              </div>
            </button>
            <button onClick={() => navigate("/pipeline")} className="bento-tile px-3.5 py-4 text-left" data-testid="kpi-deals">
              <div className="text-[9.5px] text-zinc-500 uppercase tracking-[0.25em] mb-1">Deals</div>
              <div className="h-editorial text-[22px] text-white tnum">{dealsOpen}</div>
              <div className="mt-2 flex items-center gap-1">
                <span className="w-1 h-1 rounded-full bg-emerald-400" />
                <span className="text-[9.5px] text-zinc-500 tracking-wider">OUVERTS</span>
              </div>
            </button>
            <button onClick={() => navigate("/pipeline")} className="bento-tile px-3.5 py-4 text-left" data-testid="kpi-activity">
              <div className="text-[9.5px] text-zinc-500 uppercase tracking-[0.25em] mb-1">7 jours</div>
              <div className="h-editorial text-[22px] text-emerald-300 tnum">{activity7d}</div>
              <div className="mt-2 flex items-center gap-1">
                <span className="w-1 h-1 rounded-full bg-emerald-400 spark-pulse" />
                <span className="text-[9.5px] text-zinc-500 tracking-wider">NOUVEAUX</span>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Admin banner — visible uniquement pour les super-admins */}
      {isAdmin && (
        <div className="px-5 mb-3">
          <button
            onClick={() => navigate("/prosperrboss")}
            data-testid="admin-banner"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-[var(--surface)] border border-emerald-500/40 hover:border-emerald-500 active:scale-[0.99] transition-colors"
          >
            <div className="w-8 h-8 rounded-lg bg-emerald-500/15 grid place-items-center shrink-0">
              <Shield className="w-4 h-4 text-emerald-400" strokeWidth={1.8} />
            </div>
            <div className="flex-1 text-left">
              <div className="text-[13px] font-medium text-[var(--text)]">Console admin Prosperr</div>
              <div className="text-[11px] text-zinc-500">Utilisateurs · Insights · Erreurs · Backups</div>
            </div>
            <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />
          </button>
        </div>
      )}

      {/* Empty state — pousse vers Trouver */}
      {totalLeads === 0 && (
        <div className="px-5" data-testid="onboarding-block">
          <div className="hp-card p-6 text-center">
            <div className="font-display font-semibold text-lg text-[var(--text)]">Aucun prospect pour l'instant</div>
            <p className="text-[13px] text-zinc-400 mt-2 mb-5 leading-relaxed">
              Commencez par en trouver. La méthode la plus rapide est la carte autour de vous.
            </p>
            <button onClick={() => navigate("/trouver")} className="hp-btn hp-btn-primary w-full h-12" data-testid="onboard-find">
              Trouver mes premiers prospects
            </button>
            <button onClick={() => setAddOpen(true)} className="hp-btn hp-btn-tertiary w-full h-12 mt-2" data-testid="onboard-add">
              Ajouter manuellement
            </button>
          </div>
        </div>
      )}

      {/* INBOX — bloc principal, layout dense 2026 */}
      {inbox.length > 0 && (
        <div className="px-5" data-testid="inbox-block">
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-300">À traiter</span>
              <span className="text-[10px] font-bold text-emerald-300 bg-emerald-500/12 border border-emerald-500/30 rounded-md px-1.5 tabular-nums">{inbox.length}</span>
            </div>
            <Link to="/pipeline?view=list" className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 hover:text-white transition-colors" data-testid="inbox-see-all">
              Tout voir →
            </Link>
          </div>
          <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] overflow-hidden divide-y divide-[var(--border-soft)]">
            {inbox.map((item, i) => {
              const Icon = ACTION_ICON[item.type] || Phone;
              const tag = TAGS[item.type];
              const label = item.prospect.name || item.prospect.company || "Prospect";
              return (
                <div
                  key={`${item.type}-${item.prospect.prospect_id}`}
                  data-testid={`inbox-row-${i}`}
                  className="w-full flex items-center gap-2.5 px-3.5 py-3 hover:bg-[var(--surface-alt)]/60 transition-colors text-left"
                >
                  <button
                    onClick={() => navigate(`/prospect/${item.prospect.prospect_id}`)}
                    className="flex items-center gap-3 flex-1 min-w-0 active:scale-[0.998] transition-transform text-left"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[var(--surface-alt)] border border-[var(--border-soft)] grid place-items-center shrink-0">
                      <Icon className="w-3.5 h-3.5 text-zinc-300" strokeWidth={1.8} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13.5px] font-medium text-[var(--text)] truncate leading-tight">{label}</div>
                      <div className="text-[11px] text-zinc-500 truncate mt-0.5">{whyForItem(item)}</div>
                    </div>
                    <span className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-md uppercase tracking-[0.15em] tnum border ${tag.cls}`}>
                      {tag.label}
                    </span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPrepareTarget({
                        prospectId: item.prospect.prospect_id,
                        prospect: item.prospect,
                        actionType: PREPARE_TYPE[item.type] || "auto",
                      });
                    }}
                    data-testid={`inbox-prepare-${i}`}
                    aria-label="Préparer l'action"
                    className="shrink-0 inline-flex items-center gap-1 px-2 h-7 rounded-md bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-500 hover:to-emerald-500 text-white text-[9.5px] font-bold uppercase tracking-[0.1em] transition-all active:scale-95 glow-violet"
                  >
                    <Sparkles className="w-3 h-3" strokeWidth={2.4} />
                    <span>Prépa.</span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* CTA secondaire — un seul bouton "Ajouter" pour rester simple */}
      {totalLeads > 0 && (
        <div className="px-5 mt-4">
          <button
            onClick={() => setAddOpen(true)}
            data-testid="add-prospect-cta"
            className="hp-btn hp-btn-secondary w-full h-12 flex items-center justify-center gap-2 text-[13px]"
          >
            <Plus className="w-4 h-4" strokeWidth={2} /> Ajouter un prospect
            <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </button>
        </div>
      )}

      <div className="h-20" />
      <MagicProspectButton />
      <AddProspectSheet
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={() => api.get("/prospects").then((r) => setProspects(r.data || [])).catch(() => {})}
      />
      <WelcomeSplash name={user?.name} />
      <PrepareActionSheet
        open={!!prepareTarget}
        onOpenChange={(v) => !v && setPrepareTarget(null)}
        prospectId={prepareTarget?.prospectId}
        prospect={prepareTarget?.prospect}
        actionType={prepareTarget?.actionType || "auto"}
      />
    </AppShell>
  );
}
